import PlayerHelper from './playerHelper';
import Subtitle from '@components/atoms/text/subtitle';
import Youbora from '../../../../classes/youbora';
import { MediaPlayerProps as Props } from '../../../../../shared/types/props/player';
import { playerSelector } from '@shared/store/slices/player.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { setState, mediaSelector } from '@shared/store/slices/media.slice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import {
  PlayerState,
  PlayerType,
} from '../../../../../shared/types/enums/player';
import AnalyticsInstance from '@shared/utils/analytics';
import { DrmSystem } from '@tvup/omnitrack-js';

/**
 * Checks if the AVPlayer is in one of the specified states.
 * Used to ensure operations are only performed when the player is in a valid state.
 *
 * @param {string[]} checkStates - Array of valid player states to check against
 * @returns {boolean} True if player is in one of the specified states, false otherwise
 */
const checkState = (checkStates: string[]) => {
  try {
    const currentState = webapis.avplay.getState();
    if (checkStates.includes(currentState)) return true;
  } catch (e) {
    console.error('Error trying to get state', e);
  }
  return false;
};

/**
 * MediaPlayer component for Samsung Tizen AVPlayer.
 * Manages playback using the native webapis.avplay API for Tizen Smart TVs.
 * Handles DRM, track selection, seeking, playback control, and Youbora analytics integration.
 *
 * @param {Props} props - Component props containing DRM config, headers, source, asset, channel, and position
 * @returns {JSX.Element} AVPlayer object element and subtitle component
 */
function MediaPlayer({
  drm,
  headers,
  source,
  asset,
  channel,
  position,
}: Props) {
  const dispatch = useAppDispatch();
  const youbora = useRef<Youbora | null>(null);
  const {
    state,
    position: mediaPosition,
    audio,
    bitrate,
    startover: rawStartover,
    subtitles,
    live,
  } = useAppSelector(mediaSelector);
  const { pilot } = useAppSelector(playerSelector);
  const { params } = useAppSelector(routerSelector);
  const [cue, setCue] = useState('');
  const [loaded, setLoaded] = useState(false);
  const seekingRef = useRef(false);
  // Determine if this is a startover playback (restart from beginning)
  const startover = useMemo(
    () => rawStartover || params.current?.restart || false,
    [params, rawStartover]
  );

  // Event handlers

  /**
   * Handles the completion of a seek operation.
   * Resumes playback and updates player state after seeking.
   */
  const onSeeked = () => {
    dispatch(setState(PlayerState.SEEKED));
    setTimeout(() => {
      if (checkState(['READY', 'PLAYING', 'PAUSED'])) {
        try {
          webapis.avplay.play();
        } catch (e) {
          console.error('Error trying to play after seek', e);
        }
      }
      dispatch(setState(PlayerState.PLAYING));
      seekingRef.current = false;
    });
  };

  // Effect hooks

  // Update audio track when audio selection changes
  useEffect(() => {
    if (audio?.id && parseInt(audio?.id) > 0) {
      try {
        if (checkState(['PLAYING'])) {
          try {
            webapis.avplay.setSelectTrack('AUDIO', parseInt(audio?.id));
          } catch (e) {
            console.error('Error while selecting new track', e);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [audio]);

  // Update subtitle track when subtitle selection changes
  useEffect(() => {
    if (subtitles?.id?.length) {
      try {
        if (checkState(['PLAYING', 'PAUSED'])) {
          try {
            webapis.avplay.setSelectTrack('TEXT', parseInt(subtitles?.id));
          } catch (e) {
            console.error('Error while selecting new text track', e);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [subtitles]);

  // Update Youbora analytics and bitrate when bitrate changes
  useEffect(() => {
    youbora.current?.updateOptions();
  }, [bitrate]);

  // Handle player state changes (seek, play, pause)
  useEffect(() => {
    try {
      if (state === PlayerState.SEEK) {
        seekingRef.current = true;
        if (live) {
          // For live content, use jump forward/backward instead of seek
          const EndTime = parseInt(
            webapis.avplay
              .getStreamingProperty('GET_LIVE_DURATION')
              .split('|')[1]
          );
          const currentTime =
            Date.now() - (EndTime - webapis.avplay.getCurrentTime());
          if (mediaPosition < currentTime) {
            // Jump backward if seeking to earlier position
            let jumpValue = currentTime - mediaPosition;
            if (jumpValue < 0) jumpValue = mediaPosition + 1000;
            if (checkState(['PLAYING', 'PAUSED']))
              webapis.avplay.jumpBackward(jumpValue, onSeeked, onSeeked);
          } else {
            // Jump forward if seeking to later position
            if (checkState(['PLAYING', 'PAUSED']))
              webapis.avplay.jumpForward(
                mediaPosition - currentTime,
                onSeeked,
                onSeeked
              );
          }
        } else {
          // For VOD content, use standard seek
          if (checkState(['IDLE', 'READY', 'PLAYING', 'PAUSED']))
            webapis.avplay.seekTo(mediaPosition * 1000, onSeeked, onSeeked);
        }

        if (youbora.current?.adapter?.jumpForward) {
          youbora.current.adapter.jumpForward();
        } else if (youbora.current?.adapter?.fireSeek) {
          youbora.current.adapter.fireSeek(mediaPosition * 1000);
        }
      } else if (state === PlayerState.PLAY) {
        if (!seekingRef.current) {
          if (checkState(['READY', 'PLAYING', 'PAUSED'])) webapis.avplay.play();
          dispatch(setState(PlayerState.PLAYING));
          if (youbora.current?.adapter?.playVideo) {
            youbora.current.adapter.playVideo();
          } else if (youbora.current?.adapter?.fireResume) {
            youbora.current.adapter.fireResume();
          }
        }
      } else if (state === PlayerState.PAUSE) {
        if (!seekingRef.current) {
          if (checkState(['PLAYING', 'PAUSED'])) webapis.avplay.pause();
          if (youbora.current?.adapter?.pauseVideo) {
            youbora.current.adapter.pauseVideo();
          } else if (youbora.current?.adapter?.firePause) {
            youbora.current.adapter.firePause();
          }
        }
      }
    } catch (e) {
      console.error('Error while performing media operation', e);
    }
  }, [state, mediaPosition, live]);

  // Cleanup: Destroy player and Youbora instance on unmount
  useEffect(() => {
    // Player and Youbora destruction
    return () => {
      try {
        webapis.avplay.close();
        AnalyticsInstance.getOmnitrackManager()?.forceClosePlaybackSession();
      } catch (e) {
        console.error('Error while trying to close the player', e);
      }
      if (youbora.current?.adapter?.stopVideo) {
        youbora.current.adapter.stopVideo();
      }
      if (youbora.current) {
        youbora.current.destroy();
      }
      youbora.current = null;
    };
  }, []);

  // Initialize Youbora analytics when content is loaded and startover changes
  useEffect(() => {
    if (loaded) {
      youbora.current = new Youbora({
        asset,
        channel,
        player: { instance: null, type: PlayerType.DASHJS },
      });
      youbora.current?.start();
    }
    return () => {
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, [startover]);

  // Initialize player when source changes
  useEffect(() => {
    if (source) {
      // Destroy previous youbora instance
      if (youbora.current?.adapter?.stopVideo) {
        youbora.current.adapter.stopVideo();
      }
      if (youbora.current) {
        youbora.current.destroy();
      }
      youbora.current = null;
      youbora.current = new Youbora({
        asset,
        channel,
        player: { instance: null, type: PlayerType.AVPLAYER },
      });
      AnalyticsInstance.getOmnitrackManager()?.attachSamsungAvPlay(
        drm ? DrmSystem.WIDEVINE : DrmSystem.NONE
      );
      PlayerHelper.initPlayer({
        source,
        headers,
        drm,
        isSeeking: seekingRef,
        setCue,
        youboraInstance: youbora,
        position,
      });

      setLoaded(true);
    }
    return () => {
      setCue('');
    };
  }, [source]);

  return (
    // AVPlayer object element for Tizen native playback
    <>
      <object
        id={'avPlayer'}
        className={'player-container'}
        type={'application/avplayer'}
        style={{
          width: 1920,
          height: 1080,
          position: 'absolute',
          left: 0,
          top: 0,
        }}
      />
      <Subtitle
        className={pilot ? 'pilot' : ''}
        dangerouslySetInnerHTML={{ __html: cue }}
        visible={!!subtitles?.id || subtitles?.id === 0}
      />
    </>
  );
}

export { checkState };
export default MediaPlayer;
